import React, { useState, useEffect } from 'react';
import { admin } from '../api';
import { Calendar, User, Zap, History, ArrowDownUp, Trash2, Power, AlertCircle } from 'lucide-react';

const ManageUsersPage = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionInProgress, setActionInProgress] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'email', direction: 'asc' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await admin.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users data:', error);
      setError('Failed to load users data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...users];
    if (sortConfig.key) {
      sortableUsers.sort((a, b) => {
        // Handle nulls
        if (a[sortConfig.key] === null) return sortConfig.direction === 'asc' ? 1 : -1;
        if (b[sortConfig.key] === null) return sortConfig.direction === 'asc' ? -1 : 1;
        
        if (typeof a[sortConfig.key] === 'string') {
          return sortConfig.direction === 'asc' 
            ? a[sortConfig.key].localeCompare(b[sortConfig.key])
            : b[sortConfig.key].localeCompare(a[sortConfig.key]);
        } else {
          return sortConfig.direction === 'asc' 
            ? a[sortConfig.key] - b[sortConfig.key]
            : b[sortConfig.key] - a[sortConfig.key];
        }
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const confirmMessage = currentStatus 
      ? 'Are you sure you want to deactivate this user? They will no longer be able to login.' 
      : 'Are you sure you want to reactivate this user?';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setActionInProgress(userId);
    setError('');
    setSuccess('');

    try {
      const response = await admin.toggleUserStatus(userId);
      
      // Update the users list with the new status
      setUsers(prev => 
        prev.map(user => user.id === userId ? { ...user, is_active: response.data.is_active } : user)
      );
      
      setSuccess(response.data.message);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Error toggling user status:', error);
      setError(error.response?.data?.detail || 'Failed to update user status');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to permanently delete user ${email}? This action cannot be undone.`)) {
      return;
    }

    setActionInProgress(userId);
    setError('');
    setSuccess('');

    try {
      const response = await admin.deleteUser(userId);
      
      // Remove the user from the list
      setUsers(prev => prev.filter(user => user.id !== userId));
      
      setSuccess(response.data.message);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionInProgress(null);
    }
  };

  const SortableHeader = ({ label, sortKey, icon }) => (
    <th 
      className="px-4 py-2 font-medium text-left text-gray-700 cursor-pointer hover:bg-gray-50"
      onClick={() => requestSort(sortKey)}
    >
      <div className="flex items-center">
        {icon}
        <span className="ml-1">{label}</span>
        {sortConfig.key === sortKey && (
          <ArrowDownUp className={`ml-1 h-4 w-4 text-gray-500 ${sortConfig.direction === 'asc' ? 'transform rotate-180' : ''}`} />
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">User Management</h2>
      
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-center">
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {success}
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader 
                  label="User" 
                  sortKey="email" 
                  icon={<User className="h-4 w-4 text-gray-500" />} 
                />
                <SortableHeader 
                  label="Plan" 
                  sortKey="plan_name" 
                  icon={<Zap className="h-4 w-4 text-gray-500" />} 
                />
                <SortableHeader 
                  label="Plan Activation" 
                  sortKey="plan_activation_date" 
                  icon={<Calendar className="h-4 w-4 text-gray-500" />} 
                />
                <SortableHeader 
                  label="Last Login" 
                  sortKey="last_login" 
                  icon={<History className="h-4 w-4 text-gray-500" />} 
                />
                <th className="px-4 py-2 font-medium text-left text-gray-700">Monthly Usage</th>
                <th className="px-4 py-2 font-medium text-left text-gray-700">All-Time Usage</th>
                <th className="px-4 py-2 font-medium text-center text-gray-700">Status</th>
                <th className="px-4 py-2 font-medium text-center text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-gray-900">{user.email}</div>
                      {user.full_name && <div className="text-sm text-gray-500">{user.full_name}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {user.plan_name}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(user.plan_activation_date)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(user.last_login)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div><span className="font-medium">{user.monthly_credits_used}</span> credits</div>
                      <div><span className="font-medium">{user.monthly_tokens_used}</span> tokens</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div><span className="font-medium">{user.total_credits_used}</span> credits</div>
                      <div><span className="font-medium">{user.total_tokens_used}</span> tokens</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {user.is_active !== undefined ? (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center items-center space-x-2">
                      <button
                        onClick={() => handleToggleUserStatus(user.id, user.is_active !== undefined ? user.is_active : true)}
                        disabled={actionInProgress === user.id}
                        className={`p-1 rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 ${
                          actionInProgress === user.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title={user.is_active !== undefined && user.is_active ? 'Deactivate User' : 'Activate User'}
                      >
                        <Power className={`h-5 w-5 ${
                          user.is_active !== undefined && !user.is_active ? 'text-green-500' : 'text-red-500'
                        }`} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id, user.email)}
                        disabled={actionInProgress === user.id}
                        className={`p-1 rounded-full text-red-500 hover:bg-red-100 focus:outline-none focus:bg-red-100 ${
                          actionInProgress === user.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title="Delete User"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManageUsersPage;
